// src/components/ForcePasswordChange.jsx
//
// Blocking password-rotation screen for staff whose password was chosen by
// someone else — an admin reset, or the generated one-time password issued when
// a doctor's login is auto-created. The backend sets mustChangePassword on the
// login response in those cases (V83) and clears it once the user picks their
// own password.
//
// Rendered by AppShell in place of the whole portal rather than as a banner or a
// redirect, because a credential a third party knows should not be usable to do
// work. It survives a reload (the flag is persisted with the session) and cannot
// be stepped around by navigating to another portal route.
//
// No current password is asked for: the backend does not require it during a
// forced rotation (V84). Demanding the credential being retired would strand an
// SSO user who has a dormant hash they were never told, and anyone who mislaid
// the temporary password an admin relayed — with the whole portal gated on it.
// The patient forced-change flow already works this way.
//
// Honest limitation, worth stating: this is a client-side gate. Someone holding
// a valid JWT could still call the API directly. It is not trying to stop that —
// they already have working credentials, so there is no privilege to gain. What
// actually closed the exposure was replacing the shared hardcoded doctor
// password with a per-user random one; this screen makes sure the temporary
// credential gets replaced rather than living on indefinitely.

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { navigatorAPI } from '../services/api';
import { resolveNavigatorId } from '../utils/currentNavigator';

import Icon from './Icon';
export default function ForcePasswordChange() {
  const { user, logout, clearMustChangePassword } = useAuth();

  const [newPassword,     setNewPassword]     = useState('');
  const [confirm,         setConfirm]         = useState('');
  const [showPw,          setShowPw]          = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState('');

  const mismatch = confirm.length > 0 && newPassword !== confirm;
  const tooShort = newPassword.length > 0 && newPassword.length < 8;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mismatch || tooShort) return;
    setSaving(true); setError('');
    try {
      // The JWT subject is the email for navigator accounts, so user.id may be
      // an email rather than a UUID. Shared with ProfilePage, and now asks
      // /navigators/me instead of downloading every staff row to find one.
      const id = await resolveNavigatorId(user);
      // No currentPassword: this is a FORCED rotation, so the backend does not
      // require the password being retired (see NavigatorManagementService's
      // forcedRotation branch). Asking for it would strand two real groups —
      // SSO users who have a dormant hash they were never told, and anyone who
      // mislaid the temporary password an admin relayed — behind a screen that
      // gates the entire portal. Matches what the patient forced-change already
      // sends: { newPassword } only.
      await navigatorAPI.changePassword(id, { newPassword });
      clearMustChangePassword();
    } catch (err) {
      setError(err.response?.data?.message || err.message
        || 'Could not change the password. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-canvas)', padding: '24px 16px',
    }}>
      <div className="card fade-in-up" style={{ maxWidth: 460, width: '100%' }}>
        <div className="card-title">Choose a new password</div>
        <div className="alert alert-warning" style={{ margin: '12px 0 18px' }}>
          Your password was set by someone else. Please choose your own before continuing.
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: 14 }}><Icon name="alert" size={14} /> {error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">
                New password <span className="required">*</span>
              </label>
              <input
                className="form-input" style={{ width: '100%' }}
                type={showPw ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
                autoComplete="new-password"
                required minLength={8}
              />
              {tooShort && (
                <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>
                  Must be at least 8 characters.
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                Confirm new password <span className="required">*</span>
              </label>
              <input
                className="form-input" style={{ width: '100%' }}
                type={showPw ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repeat new password"
                autoComplete="new-password"
                required
              />
              {mismatch && (
                <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>
                  Passwords do not match.
                </div>
              )}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
              color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showPw} onChange={() => setShowPw(p => !p)} />
              Show passwords
            </label>
          </div>

          <div className="form-actions">
            <button className="btn btn-primary" type="submit"
              disabled={saving || mismatch || tooShort}>
              {saving ? 'Saving…' : '✓ Set password and continue'}
            </button>
            <button className="btn btn-ghost" type="button" onClick={logout}>
              Sign out
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
